export type Variation =
  /** No comparison to show: both periods are empty, or an input was not a finite number. */
  | { kind: 'none' }
  /** Nothing in the previous period and something in this one. A percentage would be meaningless. */
  | { kind: 'new' }
  | { kind: 'change'; percent: number; direction: 'up' | 'down' | 'flat' };

/**
 * Period-over-period variation, with the denominator guarded.
 *
 * The dashboard used to compute this inline as `((current - previous) / previous) * 100`, which yields
 * Infinity when the previous period is empty and NaN when both are, then masked it with `|| 0` and a
 * special case that displayed a flat 100%. The result was a badge that looked like a real measurement
 * but was not: a producer who sold for the first time saw "100%", and a producer with no data on either
 * side saw "0%" as though nothing had changed.
 *
 * Growth from zero has no percentage. It is reported as its own kind so the UI can say so.
 */
export const resolveVariation = (
  current: number,
  previous: number,
): Variation => {
  if (!Number.isFinite(current) || !Number.isFinite(previous))
    return { kind: 'none' };

  if (previous === 0) return current === 0 ? { kind: 'none' } : { kind: 'new' };

  const percent = Math.round(((current - previous) / previous) * 100);

  return {
    kind: 'change',
    percent,
    direction: percent > 0 ? 'up' : percent < 0 ? 'down' : 'flat',
  };
};

/**
 * Whether the variation is good news for the producer.
 *
 * `invert` flips it for metrics where going up is bad - refunds, and now the refund rate. A flat
 * variation is not framed as either.
 */
export const isVariationPositive = (
  variation: Variation,
  invert = false,
): boolean | null => {
  if (variation.kind === 'none') return null;
  if (variation.kind === 'new') return !invert;
  if (variation.direction === 'flat') return null;

  return invert ? variation.direction === 'down' : variation.direction === 'up';
};
